/**
 * Active-car switcher dropdown.
 *
 * - Renders the current active car's icon + name as a button.
 * - Click opens a popover listing all accessible cars; selecting one
 *   POSTs /api/cars/{id}/active and the active state flips immediately
 *   (the underlying `useSetActiveCar` invalidates `["auth","me"]`).
 * - Below the list, a "Manage in Garage" link routes to `/garage`.
 *
 * Hidden when the user has zero cars — the page-level empty state owns
 * that affordance.
 *
 * Layout:
 *   - Desktop: rendered inside the sidebar above the Account row.
 *   - Overview: rendered in the month header so every viewport has direct
 *     access to active-car switching and "Manage in Garage".
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import CarIcon from "../components/CarIcon";
import Icon from "../components/Icon";
import { useActiveCar } from "../auth/useActiveCar";
import { useSetActiveCar } from "../api/cars";

interface CarSwitcherProps {
  /** Visual variant — currently `wide` is used by the desktop sidebar. */
  variant?: "wide";
  /** Popover direction. Sidebar uses `up` because it sits near the viewport bottom. */
  placement?: "down" | "up";
}

export default function CarSwitcher({
  variant = "wide",
  placement = "down",
}: CarSwitcherProps) {
  const { cars, activeCar, hasCars, isLoading } = useActiveCar();
  const setActive = useSetActiveCar();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside-click / Escape.
  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  if (isLoading || !hasCars || !activeCar) return null;

  function handlePick(id: number) {
    if (id !== activeCar?.id) {
      setActive.mutate(id);
    }
    setOpen(false);
  }

  return (
    <div
      ref={containerRef}
      className={`car-switcher car-switcher--${variant} car-switcher--${placement}`}
    >
      <button
        type="button"
        className="car-switcher__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="car-switcher__icon">
          {activeCar.has_image ? (
            <img
              src={`/api/cars/${activeCar.id}/image`}
              alt=""
              className="car-switcher__image"
            />
          ) : (
            <CarIcon name={activeCar.icon} size={28} />
          )}
        </span>
        <span className="car-switcher__name">{activeCar.name}</span>
        <Icon name="chevron" className="car-switcher__chevron" />
      </button>

      {open && (
        <div className="car-switcher__popover" role="listbox">
          <ul className="car-switcher__list">
            {cars.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.id === activeCar.id}
                  className={
                    "car-switcher__option" +
                    (c.id === activeCar.id
                      ? " car-switcher__option--active"
                      : "")
                  }
                  onClick={() => handlePick(c.id)}
                >
                  <span className="car-switcher__icon">
                    {c.has_image ? (
                      <img
                        src={`/api/cars/${c.id}/image`}
                        alt=""
                        className="car-switcher__image"
                      />
                    ) : (
                      <CarIcon name={c.icon} size={24} />
                    )}
                  </span>
                  <span className="car-switcher__option-body">
                    <span className="car-switcher__option-name">{c.name}</span>
                    {c.access_level !== "owner" && (
                      <span className="car-switcher__option-sub">
                        Shared by {c.owner_username}
                      </span>
                    )}
                  </span>
                  {c.id === activeCar.id && (
                    <Icon name="check" className="car-switcher__check" />
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="car-switcher__footer">
            <Link
              to="/garage"
              className="car-switcher__manage"
              onClick={() => setOpen(false)}
            >
              Manage in Garage →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
